import React, { useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowsMerge, Buildings, Check, CircleNotch, DownloadSimple, FileCsv, ListBullets, Steps, UploadSimple, UserPlus, Warning, WarningCircle,
} from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, ListenPanel, PanelZeile } from '../../../../components/immobilien/ui';
import { downloadBlob } from '../../../../lib/export/types';
import { buildImportPlan, dekodiereCsv, parseCsv, vorhandeneId, vorlageCsv } from '../../../../lib/dienstleister/import';
import { schreibeImport } from '../../../../lib/dienstleister/api';
import type { ImportPlan } from '../../../../lib/dienstleister/types';

type Schritt = { nr: 1 } | { nr: 2; plan: ImportPlan } | { nr: 3; ergebnis: { dienstleister: number; objektLinks: number; uebersprungen: number; fehler: string[] } };

const FORMAT = 'Format: Objekt-Nummer;Beschreibung;Name;Kategorien;von;bis;Straße;PLZ;Stadt;Ansprechpartner;E-Mail;Telefon;Saldo;Vertragsart';

/** CSV-Import (Handoff Dienstleister §7, Screens 04–06) – bestehende Logik lib/dienstleister/import.ts + schreibeImport. */
export function ImportPanel({ bestehende, objekte, kategorien, onSchliessen, onImportiert, onListe }: {
  bestehende: readonly { id: string; name: string; email: string | null }[];
  /** vorhandene Kategorien – unbekannte werden beim Import übersprungen */
  kategorien: readonly { name: string }[];
  objekte: readonly { id: string; objektnummer?: string | null; bezeichnung?: string | null }[];
  onSchliessen: () => void;
  onImportiert: () => void;
  /** „Liste anzeigen“: Filter zurücksetzen und zur Liste */
  onListe: () => void;
}) {
  const [schritt, setSchritt] = useState<Schritt>({ nr: 1 });
  const kpi = schritt.nr === 1 ? 'Datei wählen' : schritt.nr === 2 ? 'Plan prüfen' : 'Fertig';
  const nummern = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of objekte) if (o.objektnummer?.trim()) m[o.objektnummer.trim()] = o.id;
    return m;
  }, [objekte]);
  return (
    <ListenPanel icon={FileCsv} titel="Dienstleister CSV-Import" sub={FORMAT} onSchliessen={onSchliessen}>
      <KpiReihe>
        <KpiTile layout="kachel" icon={Steps} ton="acc" wert={`${schritt.nr} / 3`} label={kpi} />
      </KpiReihe>
      {schritt.nr === 1 && <DateiSchritt onPlan={text => setSchritt({ nr: 2, plan: buildImportPlan(parseCsv(text), nummern) })} />}
      {schritt.nr === 2 && (
        <PlanSchritt plan={schritt.plan} bestehende={bestehende} objekte={objekte} kategorien={kategorien} onZurueck={() => setSchritt({ nr: 1 })}
          onFertig={ergebnis => { onImportiert(); setSchritt({ nr: 3, ergebnis }); }} />
      )}
      {schritt.nr === 3 && (
        <>
          <KpiReihe>
            <KpiTile layout="kachel" icon={UserPlus} ton="ok" wert={schritt.ergebnis.dienstleister} label="Dienstleister erstellt" />
            <KpiTile layout="kachel" icon={Buildings} ton="acc" wert={schritt.ergebnis.objektLinks} label="Objekt-Zuordnungen" />
            <KpiTile layout="kachel" icon={Warning} ton="warn" wert={schritt.ergebnis.uebersprungen} label="Übersprungen" />
          </KpiReihe>
          {schritt.ergebnis.fehler.length > 0 && (
            <InnenKarte padding="14px 12px 10px" gap={4}>
              <div style={{ padding: '0 6px 6px' }}><KartenTitel>Fehler</KartenTitel></div>
              <div className="flex flex-col" style={{ maxHeight: 240, overflowY: 'auto' }}>
                {schritt.ergebnis.fehler.map((m, i) => <PanelZeile key={i} icon={WarningCircle} ton="bad" titel={m} />)}
              </div>
            </InnenKarte>
          )}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton groesse={44} art="akzent" icon={Check} onClick={onSchliessen}>Schließen</ImmoButton>
            <ImmoButton groesse={44} icon={ListBullets} onClick={onListe}>Liste anzeigen</ImmoButton>
          </div>
        </>
      )}
    </ListenPanel>
  );
}

function DateiSchritt({ onPlan }: { onPlan: (text: string) => void }) {
  const eingabe = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [liest, setLiest] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const pruefe = (inhalt: string) => {
    if (parseCsv(inhalt).length === 0) { setFehler('Keine Datenzeilen gefunden – erste Zeile ist die Kopfzeile, Trennzeichen Semikolon.'); return; }
    setFehler(null);
    onPlan(inhalt);
  };
  const datei = async (f: File) => {
    setLiest(true);
    try { pruefe(dekodiereCsv(await f.arrayBuffer())); } catch (e) { setFehler(`Datei konnte nicht gelesen werden: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`); } finally { setLiest(false); }
  };
  return (
    <>
      <InnenKarte>
        <KartenTitel>CSV-Datei wählen</KartenTitel>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
          CSV-Datei auswählen (.csv) oder Inhalt einfügen. Pro Zeile eine Objekt-Zuordnung; gleiche Dienstleister (Name + E-Mail) werden zusammengefasst. Kategorien kommagetrennt, ohne Angabe „Sonstige Dienstleister“.
        </span>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton art="akzent" icon={liest ? CircleNotch : UploadSimple} disabled={liest} onClick={() => eingabe.current?.click()}>{liest ? 'Datei wird gelesen …' : 'CSV-Datei auswählen'}</ImmoButton>
          <ImmoButton icon={DownloadSimple} onClick={() => downloadBlob(new Blob([vorlageCsv()], { type: 'text/csv;charset=utf-8;' }), 'dienstleister_vorlage.csv')}>Vorlage herunterladen</ImmoButton>
        </div>
        <input ref={eingabe} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void datei(f); }} />
      </InnenKarte>
      <InnenKarte>
        <KartenTitel>… oder CSV-Inhalt hier einfügen</KartenTitel>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={6} aria-label="CSV-Inhalt"
          placeholder="Objekt-Nummer;Beschreibung;Name;Kategorien;…"
          style={{ width: '100%', borderRadius: 15, padding: 14, fontSize: 12, fontFamily: 'ui-monospace, monospace', background: 'var(--bg)', color: 'var(--ink)', border: 'none', boxShadow: 'inset 0 0 0 1px var(--line)', resize: 'vertical' }} />
        {fehler && <span role="alert" style={{ fontSize: 12, color: 'var(--bad)' }}>{fehler}</span>}
        <ImmoButton icon={Steps} className="self-start" disabled={!text.trim()} onClick={() => pruefe(text)}>Plan erstellen</ImmoButton>
      </InnenKarte>
    </>
  );
}

function PlanSchritt({ plan, bestehende, objekte, kategorien, onZurueck, onFertig }: {
  plan: ImportPlan;
  kategorien: readonly { name: string }[];
  bestehende: readonly { id: string; name: string; email: string | null }[];
  objekte: readonly { id: string; objektnummer?: string | null; bezeichnung?: string | null }[];
  onZurueck: () => void;
  onFertig: (e: { dienstleister: number; objektLinks: number; uebersprungen: number; fehler: string[] }) => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const objektName = new Map(objekte.map(o => [o.id, [o.objektnummer, o.bezeichnung].filter(Boolean).join(' · ')]));
  const links = plan.dienstleister.reduce((s, d) => s + d.objektLinks.length, 0);
  const bekannt = new Set(kategorien.map(k => k.name.trim().toLowerCase()));
  const unbekannt = [...new Set(plan.dienstleister.flatMap(d => d.kategorien).filter(k => !bekannt.has(k.trim().toLowerCase())))];
  const schreiben = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const r = await schreibeImport(plan);
      onFertig({ dienstleister: r.dienstleister, objektLinks: r.objektLinks, uebersprungen: plan.ohneObjektMatch.length, fehler: r.fehler });
    } catch (e) { setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler'); } finally { setLaeuft(false); }
  };
  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" icon={UserPlus} ton="acc" wert={plan.dienstleister.length} label="Dienstleister (dedupliziert)" />
        <KpiTile layout="kachel" icon={Buildings} ton="info" wert={links} label="Objekt-Zuordnungen" />
        <KpiTile layout="kachel" icon={Warning} ton="warn" wert={plan.ohneObjektMatch.length} label="Zeilen ohne Objekt-Match" sub="werden übersprungen" />
      </KpiReihe>
      <InnenKarte padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Plan</KartenTitel></div>
        {plan.dienstleister.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Zeile passt zu einem Objekt.</span>}
        <div className="flex flex-col" style={{ maxHeight: 360, overflowY: 'auto' }}>
          {plan.dienstleister.map(d => {
            const vorhanden = vorhandeneId(d, bestehende) !== null;
            return (
              <PanelZeile key={`${d.name}|${d.email ?? ''}`} icon={vorhanden ? ArrowsMerge : UserPlus} ton={vorhanden ? 'info' : 'ok'} titel={d.name}
                sub={[d.kategorien.join(', '), d.objektLinks.map(l => [objektName.get(l.objekt_id), l.vertragsart ? `(${l.vertragsart})` : ''].filter(Boolean).join(' ')).join(', ')].filter(Boolean).join(' · ')}
                chip={vorhanden ? { text: 'vorhanden – Zuordnung ergänzen', ton: 'info' } : { text: 'neu', ton: 'ok' }} />
            );
          })}
        </div>
      </InnenKarte>
      {plan.ohneObjektMatch.length > 0 && (
        <InnenKarte padding="14px 12px 10px" gap={4}>
          <div style={{ padding: '0 6px 6px' }}><KartenTitel>Ohne Objekt-Match</KartenTitel></div>
          <div className="flex flex-col" style={{ maxHeight: 220, overflowY: 'auto' }}>
            {plan.ohneObjektMatch.map((r, i) => (
              <PanelZeile key={i} icon={Warning} ton="warn" titel={r.name || 'Ohne Namen'} sub={`${r.beschreibung || 'ohne Beschreibung'} – nicht gefunden`} />
            ))}
          </div>
        </InnenKarte>
      )}
      {unbekannt.length > 0 && (
        <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--warn)' }}>
          Unbekannte Kategorien werden nicht zugeordnet: {unbekannt.join(', ')} – vorher unter „Kategorien &amp; Regeln“ anlegen.
        </span>
      )}
      {fehler && <span role="alert" style={{ fontSize: 12, color: 'var(--bad)' }}>{fehler}</span>}
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} icon={ArrowLeft} onClick={onZurueck} disabled={laeuft}>Zurück</ImmoButton>
        <ImmoButton groesse={44} art="akzent" icon={laeuft ? CircleNotch : Check} disabled={laeuft || plan.dienstleister.length === 0} onClick={() => { void schreiben(); }}>
          {laeuft ? 'Import wird geschrieben …' : 'Import schreiben'}
        </ImmoButton>
      </div>
    </>
  );
}
